import { type ButtonHTMLAttributes } from 'react'
import { useTranslation } from 'react-i18next'

// Sign-in with an identity provider. Deliberately not a `<Button>` variant:
// providers' brand rules fix the look (Google: the four-colour G on a white or
// neutral button), so these never take our green primary. Brand colours in
// the logos are exempt from the token rule.

export type Provider = 'google'

type Props = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> & {
  provider: Provider
}

const LABEL_KEY: Record<Provider, string> = {
  google: 'auth.login.google',
}

const ProviderButton = ({ provider, className = '', ...rest }: Props) => {
  const { t } = useTranslation()
  return (
    <button
      type="button"
      className={`
        w-full inline-flex items-center justify-center gap-3
        bg-paper hover:bg-ink-100/40 disabled:opacity-60
        text-ink-900 font-semibold text-base
        border border-ink-200 rounded-lg
        px-4 py-3 min-h-[48px]
        transition-colors
        ${className}
      `}
      {...rest}
    >
      {provider === 'google' && <GoogleMark />}
      {t(LABEL_KEY[provider])}
    </button>
  )
}

const GoogleMark = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
    <path
      fill="#FFC107"
      d="M43.6 20.5H42V20H24v8h11.3C33.6 32.9 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.2-.1-2.3-.4-3.5z"
    />
    <path
      fill="#FF3D00"
      d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 7.1 29.3 5 24 5 16.3 5 9.7 9 6.3 14.7z"
    />
    <path
      fill="#4CAF50"
      d="M24 44c5.2 0 9.9-2 13.4-5.3l-6.2-5.2C29.2 35 26.7 36 24 36c-5.2 0-9.6-3.1-11.3-7.6l-6.6 5.1C9.6 39.9 16.2 44 24 44z"
    />
    <path
      fill="#1976D2"
      d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.2 5.5l6.2 5.2C41.4 35.2 44 30.1 44 24c0-1.2-.1-2.3-.4-3.5z"
    />
  </svg>
)

export default ProviderButton
